/**
 * MobX store for filler word removal state.
 */
import { makeAutoObservable, runInAction } from 'mobx';
import { 
    FillerWordOccurrence,
    FILLER_WORD_CATEGORIES,
    findFillerMatchAt,
    detectRepeatedWords,
} from './fillerWord';
import { V2TranscriptWord } from '../V2TextEditor';
import { CompositionService } from '../../../librettoEditor/services/CompositionService';
import { ProjectAssetsStore } from '../../../state/ProjectAssetsStore';
import { isScriptClip, ScriptLayerClip } from '../../../engine/clips';
import {computeFillerPaddingSec} from "./computeFillerPaddingSec";

// Type for the GetTranscript function from useAssetStore hook
type GetTranscriptFn = (params: { transcriptId: string; projectId: string }) => Promise<any | undefined>;

export type FillerWordCutMode = 'ignore' | 'delete' | 'gap' | 'transcript-only';

class FillerWordStoreImpl {
    // Whether filler word removal mode is active
    isActive: boolean = false;
    
    // Current occurrences found
    occurrences: FillerWordOccurrence[] = [];
    
    // Currently focused occurrence index
    currentIndex: number = 0;
    
    // Which filler word categories are enabled
    enabledCategories: Set<string> = new Set(FILLER_WORD_CATEGORIES.map(c => c.id));
    
    // Whether to include repeated words
    includeRepeatedWords: boolean = false;
    
    // Avoid harsh cuts toggle (default OFF - show all)
    avoidHarshCuts: boolean = false;
    
    // Whether audio analysis is in progress
    isAnalyzing: boolean = false;
    
    // Progress of audio analysis (0-100)
    analysisProgress: number = 0;
    
    // Whether removal is in progress
    isRemoving: boolean = false;
    
    // Progress of removal (0-100)
    removalProgress: number = 0;
    
    // Cut mode for removal
    cutMode: FillerWordCutMode = 'delete';
    
    // Counts per category
    categoryCounts: Map<string, number> = new Map();
    
    // All transcript words (for reference)
    private allWords: V2TranscriptWord[] = [];

    // Track whether current occurrences have been analyzed for harsh cuts.
    private scanGeneration: number = 0;
    private analyzedGeneration: number = -1;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    /**
     * Activate filler word removal mode and scan for occurrences.
     */
    activate(words: V2TranscriptWord[]) {
        this.isActive = true;
        this.allWords = words;
        this.scanForFillerWords();
    }
    
    /**
     * Activate filler word removal mode by fetching words from the script clip.
     * This fetches transcript data from the backend.
     * @param getTranscript - The GetTranscript function from useAssetStore hook
     */
    async activateAsync(getTranscript: GetTranscriptFn) {
        // Get the script layer info from CompositionStateStore
        const scriptLayerInfo = CompositionService.composition.scriptLayer;
        const scriptClipId = scriptLayerInfo?.clipId;

        if (!scriptClipId) {
            return;
        }

        const clip = CompositionService.getClipById(scriptClipId);
        if (!clip || !isScriptClip(clip)) {
            return;
        }
        
        const scriptClip = clip as ScriptLayerClip;
        const audioEntries = scriptClip.getAudioEntries();
        const projectId = ProjectAssetsStore.projectId as string;
        
        // Use derived segments to filter out words that are no longer "visible/playable"
        // (e.g., removed by cuts or replaced with gaps).
        // NOTE: These times are in script-clip local time (the same domain as WordNode.__payload.startSec/endSec).
        const scriptSegments = (scriptClip as any).scriptSegments;
	        const segments: Array<{ srcStartSec: number; srcEndSec: number; isGap?: boolean }> =
	            // Prefer scriptSegments because it includes `isGap`.
	            Array.isArray(scriptSegments) && scriptSegments.length > 0
	                ? scriptSegments
	                : // Fallback for older engine shapes.
	                  (Array.isArray((scriptClip as any).segments) ? (scriptClip as any).segments : []);

	        // Transcript-only edits (do NOT affect playback): hidden ranges + word corrections.
	        const transcriptHiddenRanges: Array<{ startSec: number; endSec: number }> = Array.isArray(
	            (scriptClip as any).transcriptHiddenRanges
	        )
	            ? ((scriptClip as any).transcriptHiddenRanges as Array<{ startSec: number; endSec: number }>)
	            : [];
	        const wordCorrections: Array<{ sourceIndex?: number; srcStartSec?: number; srcEndSec?: number; correctedText?: string }> =
	            Array.isArray((scriptClip as any).wordCorrections)
	                ? ((scriptClip as any).wordCorrections as any[])
	                : [];
	        const correctionByKey = new Map<string, string>();
	        for (const c of wordCorrections) {
	            if (c == null) continue;
	            const text = String((c as any).correctedText ?? '').trim();
	            if (!text) continue;
	            const sourceIndex = Number((c as any).sourceIndex);
	            const srcStartSec = Number((c as any).srcStartSec);
	            const srcEndSec = Number((c as any).srcEndSec);
	            if (!Number.isFinite(sourceIndex) || !Number.isFinite(srcStartSec) || !Number.isFinite(srcEndSec)) continue;
	            const k = `${sourceIndex}:${srcStartSec}:${srcEndSec}`;
	            correctionByKey.set(k, text);
	        }
	        const isHiddenAt = (t: number): boolean => {
	            if (!transcriptHiddenRanges.length) return false;
	            for (const r of transcriptHiddenRanges) {
	                const s = Number((r as any)?.startSec ?? 0);
	                const e = Number((r as any)?.endSec ?? s);
	                if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) continue;
	                if (t >= s && t < e) return true;
	            }
	            return false;
	        };
        
        // Helper to check if a word (by clip-local time) falls within a non-gap segment.
        const isWordInPlayableSection = (clipStart: number, clipEnd: number): boolean => {
            if (segments.length === 0) return true; // No segments means no cuts
            
            // Word must be fully contained within a non-gap segment
            for (const seg of segments) {
                if (seg.isGap) continue; // Skip gap segments (cut sections)
                if (clipStart >= seg.srcStartSec && clipEnd <= seg.srcEndSec) {
                    return true;
                }
            }
            return false;
        };
        
        const allWords: V2TranscriptWord[] = [];
        const MIN_ENERGY_RATIO = 0.2;
        const toFiniteNumber = (value: any, fallback: number): number => {
            const n = Number(value);
            return Number.isFinite(n) ? n : fallback;
        };
        
        // Fetch transcripts for each audio source
        for (let sourceIndex = 0; sourceIndex < audioEntries.length; sourceIndex++) {
            const entry = audioEntries[sourceIndex];
            const assetId = entry.assetId;
            if (!assetId) continue;
            
            const range = entry.range;
            const offsetSec = entry.offsetSec ?? 0;
            const playbackRateRaw = Number((entry as any).playbackRate ?? 1);
            const playbackRate = Number.isFinite(playbackRateRaw) && playbackRateRaw > 0 ? playbackRateRaw : 1;
            
            // Get source duration for range defaults
            const trackRecord = ProjectAssetsStore.projectTracks.find(
                (t: any) => t.id === assetId || t.trackId === assetId
            );
            const sourceDuration = trackRecord?.duration ?? 0;
            const rangeInSec = range?.inSec ?? 0;
            const rangeOutSec = range?.outSec ?? (sourceDuration > 0 ? sourceDuration : Infinity);
            
            try {
                // Get transcriptId from track record
                const transcriptId = trackRecord?.activeTranscriptId;
                if (!transcriptId) {
                    continue;
                }
                const transcriptJson = await getTranscript({ transcriptId, projectId });

                if (!transcriptJson?.words || !Array.isArray(transcriptJson.words)) {
                    continue;
                }
                
                // Convert and filter words
                for (const w of transcriptJson.words) {
                    if ((w.type ?? 'word') !== 'word') continue;

                    // Filter out faint words if energy data is present
                    if (w.energy?.ratio != null && w.energy.ratio > 0 && w.energy.ratio < MIN_ENERGY_RATIO) {
                        continue;
                    }
                    
                    // Support both t0/t1 (new) and start/end (legacy) field names
                    const wordStartSrc = toFiniteNumber(w.t0 ?? w.start, 0);
                    const wordEndSrc = toFiniteNumber(w.t1 ?? w.end, wordStartSrc);
                    
                    // Only include words within the source range
                    if (wordStartSrc < rangeInSec || wordEndSrc > rangeOutSec) continue;
                    
                    // Map source transcript time → script-clip local time.
                    // clipLocal = offsetSec + (srcTime - rangeInSec) / playbackRate
                    const wordStart = offsetSec + (wordStartSrc - rangeInSec) / playbackRate;
	                    const wordEnd = offsetSec + (wordEndSrc - rangeInSec) / playbackRate;
	                    
	                    // Check if word falls within a playable (non-cut) section (clip-local domain)
	                    if (!isWordInPlayableSection(wordStart, wordEnd)) {
	                        continue; // Skip words in cut regions
	                    }

	                    // Transcript-only hidden ranges: omit from analysis.
	                    if (transcriptHiddenRanges.length && isHiddenAt(wordStart)) {
	                        continue;
	                    }

	                    const correctionKey = `${sourceIndex}:${wordStartSrc}:${wordEndSrc}`;
	                    const corrected = correctionByKey.get(correctionKey);
	                    const wordText = corrected ?? String(w.text ?? w.word ?? '').trim();
	                    
	                    allWords.push({
	                        text: wordText,
	                        // Script-clip local time (stable across cuts; used for selection + cutting)
	                        startSec: wordStart,
	                        endSec: wordEnd,
	                        // Original source times (for reference / debugging)
                        srcStartSec: wordStartSrc,
                        srcEndSec: wordEndSrc,
                        sourceIndex,  // Track which audio source this word came from
                    });
                }
            } catch (err) {
                // Skip failed transcript fetch
            }
        }

        // Sort by time
        allWords.sort((a, b) => a.startSec - b.startSec);

        runInAction(() => {
            this.isActive = true;
            this.allWords = allWords;
            this.scanForFillerWords();
        });
    }

    /**
     * Deactivate filler word removal mode.
     */
    deactivate() {
        this.isActive = false;
        this.occurrences = [];
        this.currentIndex = 0;
        this.categoryCounts.clear();
        this.isAnalyzing = false;
        this.analysisProgress = 0;
        this.isRemoving = false;
        this.removalProgress = 0;
    }
    
    /**
     * Rescan filler words using the provided getTranscript function.
     * This is useful after removals to get fresh data based on current cuts.
     */
    async rescan(getTranscript: GetTranscriptFn) {
        if (!this.isActive) return;

        // Reset state but keep settings
        const savedCategories = new Set(this.enabledCategories);
        const savedIncludeRepeated = this.includeRepeatedWords;
        const savedAvoidHarsh = this.avoidHarshCuts;
        const savedCutMode = this.cutMode;

        // Re-fetch and scan
        await this.activateAsync(getTranscript);

        // Restore settings
        runInAction(() => {
            this.enabledCategories = savedCategories;
            this.includeRepeatedWords = savedIncludeRepeated;
            this.avoidHarshCuts = savedAvoidHarsh;
            this.cutMode = savedCutMode;

            // Re-scan with restored settings
            this.scanForFillerWords();
        });
    }

    private maybeAnalyzeForSmoothCuts() {
        if (!this.isActive) return;
        if (!this.avoidHarshCuts) return;
        if (this.isAnalyzing) return;
        if (this.occurrences.length === 0) return;
        if (this.analyzedGeneration === this.scanGeneration) return;
        void this.analyzeAllOccurrences();
    }
    
    /**
     * Start tracking removal progress.
     */
    startRemoval() {
        this.isRemoving = true;
        this.removalProgress = 0;
    }
    
    /**
     * Update removal progress (0-100).
     */
    updateRemovalProgress(progress: number) {
        this.removalProgress = Math.round(progress);
    }
    
    /**
     * Finish removal progress.
     */
    finishRemoval() {
        this.isRemoving = false;
        this.removalProgress = 100;
    }
    
    /**
     * Run audio analysis on all occurrences to determine harshness using the scoring system.
     * Updates isHarshCut on each occurrence based on actual audio analysis.
     */
    async analyzeAllOccurrences() {
        if (this.occurrences.length === 0) return;

        // Get the script clip for audio analysis
        const scriptClip = CompositionService.getScriptLayerClip() as ScriptLayerClip | null;
        if (!scriptClip || typeof scriptClip.analyzeCut !== 'function') {
            return;
        }

        const generationAtStart = this.scanGeneration;

        runInAction(() => {
            this.isAnalyzing = true;
            this.analysisProgress = 0;
        });
        
        const total = this.occurrences.length;
        let analyzed = 0;
        
        for (const occurrence of this.occurrences) {
            // Abort if user turned smooth cuts off or tool was closed.
            if (!this.isActive || !this.avoidHarshCuts) break;
            try {
                // Calculate padded cut boundaries
                const pad = occurrence.paddingSec;
                let cutStart = occurrence.startSec - pad;
                let cutEnd = occurrence.endSec + pad;
                
                // Clamp to not exceed neighbor boundaries
                if (occurrence.prevWordEndSec !== null) {
                    cutStart = Math.max(cutStart, occurrence.prevWordEndSec);
                }
                if (occurrence.nextWordStartSec !== null) {
                    cutEnd = Math.min(cutEnd, occurrence.nextWordStartSec);
                }
                
                // Run audio analysis
                const analysis = await scriptClip.analyzeCut(
                    cutStart,
                    cutEnd,
                    occurrence.sourceIndex,
                    occurrence.prevWordEndSec,
                    occurrence.nextWordStartSec
                );
                
                if (analysis) {
                    // Update harshness based on scoring system
                    runInAction(() => {
                        occurrence.isHarshCut = analysis.isHarsh;
                    });
                }
            } catch (err) {
                // Skip failed analysis
            }
            
            analyzed++;
            runInAction(() => {
                this.analysisProgress = Math.round((analyzed / total) * 100);
            });
        }

        runInAction(() => {
            this.isAnalyzing = false;
            this.analysisProgress = 100;
            this.analyzedGeneration = generationAtStart;
        });

        // If occurrences changed mid-analysis, rerun once for the latest scan.
        if (this.isActive && this.avoidHarshCuts && this.scanGeneration !== generationAtStart) {
            this.maybeAnalyzeForSmoothCuts();
        }
    }
    
    /**
     * Scan transcript for filler words based on current settings.
     */
    scanForFillerWords() {
        const words = this.allWords;
        const newOccurrences: FillerWordOccurrence[] = [];
        const counts = new Map<string, number>();
        
        // Initialize counts
        for (const category of FILLER_WORD_CATEGORIES) {
            counts.set(category.id, 0);
        }
        
        for (let i = 0; i < words.length; i++) {
            const match = findFillerMatchAt(words, i, this.enabledCategories);
            if (!match) continue;

            const matchEndIndex = i + match.wordCount - 1;
            const matchedWords = words.slice(i, matchEndIndex + 1);
            const firstWord = matchedWords[0];
            const lastWord = matchedWords[matchedWords.length - 1];
            if (!firstWord || !lastWord) continue;

            // Never match phrase tokens across different source tracks.
            const sourceIndex = firstWord.sourceIndex ?? 0;
            const spansMultipleSources = matchedWords.some(w => (w.sourceIndex ?? 0) !== sourceIndex);
            if (spansMultipleSources) {
                continue;
            }

            const wordText = matchedWords.map(w => w.text).join(' ');
            const categoryId = match.categoryId;
            
            // Get context based on time (2 seconds before and after)
            // This limits context when there are long silences between words
            const CONTEXT_SECONDS = 2.0;
            const contextStartTime = firstWord.startSec - CONTEXT_SECONDS;
            const contextEndTime = (lastWord.endSec ?? lastWord.startSec) + CONTEXT_SECONDS;
            
            const contextBefore = words
                .filter((w, idx) => idx < i && w.startSec >= contextStartTime)
                .map(w => w.text);
            const contextAfter = words
                .filter((w, idx) => idx > matchEndIndex && w.startSec <= contextEndTime)
                .map(w => w.text);
            
            // Get adjacent word times (both composition and source time)
            const prevWord = i > 0 ? words[i - 1] : null;
            const nextWord = matchEndIndex < words.length - 1 ? words[matchEndIndex + 1] : null;
            const prevWordEndSec = prevWord?.endSec ?? null;
            const nextWordStartSec = nextWord?.startSec ?? null;
            const prevWordSrcEndSec = prevWord?.srcEndSec ?? prevWord?.endSec ?? null;
            const nextWordSrcStartSec = nextWord?.srcStartSec ?? nextWord?.startSec ?? null;
            
            // Compute dynamic padding based on available gap (needs filler times!)
            const fillerStart = firstWord.startSec;
            const fillerEnd = lastWord.endSec ?? lastWord.startSec;
            const padding = computeFillerPaddingSec(fillerStart, fillerEnd, prevWordEndSec, nextWordStartSec);
            
            // isHarshCut will be determined by audio analysis later
            // Initially set to false - audio analysis will update this
            const occurrence: FillerWordOccurrence = {
                id: `filler_${i}_${firstWord.startSec}_${matchEndIndex}`,
                word: wordText,
                categoryId,
                startSec: firstWord.startSec,
                endSec: lastWord.endSec ?? lastWord.startSec,
                srcStartSec: firstWord.srcStartSec ?? firstWord.startSec,
                srcEndSec: lastWord.srcEndSec ?? lastWord.endSec ?? lastWord.startSec,
                sourceIndex,
                contextBefore,
                contextAfter,
                prevWordEndSec,
                nextWordStartSec,
                prevWordSrcEndSec,
                nextWordSrcStartSec,
                isHarshCut: false,  // Will be updated by analyzeAllOccurrences()
                paddingSec: padding,
            };
            
            newOccurrences.push(occurrence);
            counts.set(categoryId, (counts.get(categoryId) || 0) + 1);

            // Skip the words consumed by this phrase match.
            i = matchEndIndex;
        }
        
        // Handle repeated words if enabled
        if (this.includeRepeatedWords) {
            const repeats = detectRepeatedWords(words.map(w => ({
                text: w.text,
                startSec: w.startSec,
                endSec: w.endSec ?? w.startSec,
            })));
            
            for (const { firstIndex, secondIndex } of repeats) {
                const word = words[secondIndex]; // Remove the second occurrence
                const prevWord = words[firstIndex];
                const nextWord = words[secondIndex + 1];
                
                const prevEndSec = prevWord?.endSec ?? null;
                const nextStartSec = nextWord?.startSec ?? null;
                const prevSrcEndSec = prevWord?.srcEndSec ?? prevWord?.endSec ?? null;
                const nextSrcStartSec = nextWord?.srcStartSec ?? nextWord?.startSec ?? null;
                
                // Compute dynamic padding (needs filler times!)
                const fillerStart = word.startSec;
                const fillerEnd = word.endSec ?? word.startSec;
                const padding = computeFillerPaddingSec(fillerStart, fillerEnd, prevEndSec, nextStartSec);
                
                // isHarshCut will be determined by audio analysis later
                // Get context based on time (2 seconds before and after)
                const CONTEXT_SECONDS = 2.0;
                const contextStartTime = word.startSec - CONTEXT_SECONDS;
                const contextEndTime = (word.endSec ?? word.startSec) + CONTEXT_SECONDS;
                
                const contextBefore = words
                    .filter((w, idx) => idx < secondIndex && w.startSec >= contextStartTime)
                    .map(w => w.text);
                const contextAfter = words
                    .filter((w, idx) => idx > secondIndex && w.startSec <= contextEndTime)
                    .map(w => w.text);
                
                newOccurrences.push({
                    id: `repeat_${secondIndex}_${word.startSec}`,
                    word: word.text,
                    categoryId: 'repeated',
                    startSec: word.startSec,
                    endSec: word.endSec ?? word.startSec,
                    srcStartSec: word.srcStartSec ?? word.startSec,
                    srcEndSec: word.srcEndSec ?? word.endSec ?? word.startSec,
                    sourceIndex: word.sourceIndex ?? 0,
                    contextBefore,
                    contextAfter,
                    prevWordEndSec: prevEndSec,
                    nextWordStartSec: nextStartSec,
                    prevWordSrcEndSec: prevSrcEndSec,
                    nextWordSrcStartSec: nextSrcStartSec,
                    isHarshCut: false,  // Will be updated by analyzeAllOccurrences()
                    paddingSec: padding,
                    isRepeatedWord: true,
                    repeatedWith: `word_${firstIndex}`,
                });
            }
            
            counts.set('repeated', repeats.length);
        }
        
        // Sort by time
        newOccurrences.sort((a, b) => a.startSec - b.startSec);

        runInAction(() => {
            this.occurrences = newOccurrences;
            this.categoryCounts = counts;
            this.currentIndex = 0;
            this.scanGeneration++;
        });

        // If Smooth cuts is enabled, ensure analysis runs for the newly-scanned occurrences.
        this.maybeAnalyzeForSmoothCuts();
    }

    /**
     * Toggle a filler word category.
     */
    toggleCategory(categoryId: string) {
        if (this.enabledCategories.has(categoryId)) {
            this.enabledCategories.delete(categoryId);
        } else {
            this.enabledCategories.add(categoryId);
        }
        this.scanForFillerWords();
    }
    
    /**
     * Select all categories.
     */
    selectAllCategories() {
        this.enabledCategories = new Set(FILLER_WORD_CATEGORIES.map(c => c.id));
        this.scanForFillerWords();
    }
    
    /**
     * Deselect all categories.
     */
    deselectAllCategories() {
        this.enabledCategories.clear();
        this.scanForFillerWords();
    }
    
    /**
     * Toggle repeated words inclusion.
     */
    toggleRepeatedWords() {
        this.includeRepeatedWords = !this.includeRepeatedWords;
        this.scanForFillerWords();
    }

    /**
     * Toggle avoid harsh cuts.
     * When turning ON, triggers audio analysis if not already done.
     */
    toggleAvoidHarshCuts() {
        this.avoidHarshCuts = !this.avoidHarshCuts;
        // Reset index since filtered list changed
        this.currentIndex = 0;
        
        // If turning on and we haven't analyzed yet, run analysis
        if (this.avoidHarshCuts && !this.isAnalyzing) {
            this.maybeAnalyzeForSmoothCuts();
        }
    }

    /**
     * Set the cut mode.
     */
    setCutMode(mode: FillerWordCutMode) {
        this.cutMode = mode;
    }

    /**
     * Navigate to next occurrence.
     */
    goToNext() {
        if (this.filteredOccurrences.length === 0) return;
        this.currentIndex = (this.currentIndex + 1) % this.filteredOccurrences.length;
    }

    /**
     * Navigate to previous occurrence.
     */
    goToPrevious() {
        if (this.filteredOccurrences.length === 0) return;
        this.currentIndex = (this.currentIndex - 1 + this.filteredOccurrences.length) % this.filteredOccurrences.length;
    }

    /**
     * Go to a specific occurrence by index.
     */
    goToIndex(index: number) {
        if (index >= 0 && index < this.filteredOccurrences.length) {
            this.currentIndex = index;
        }
    }

    /**
     * Get the current occurrence.
     */
    get currentOccurrence(): FillerWordOccurrence | null {
        return this.filteredOccurrences[this.currentIndex] ?? null;
    }
    
    /**
     * Get total count of ALL occurrences (including harsh).
     */
    get totalCount(): number {
        return this.occurrences.length;
    }
    
    /**
     * Get count of harsh cuts.
     */
    get harshCount(): number {
        return this.occurrences.filter(o => o.isHarshCut).length;
    }
    
    /**
     * Get count of non-harsh (safe) cuts.
     */
    get safeCount(): number {
        return this.occurrences.filter(o => !o.isHarshCut).length;
    }
    
    /**
     * Get count of removable occurrences (based on avoidHarshCuts toggle).
     */
    get removableCount(): number {
        if (this.avoidHarshCuts) {
            return this.safeCount;
        }
        return this.totalCount;
    }
    
    /**
     * Get filtered occurrences based on avoidHarshCuts setting.
     * This is what should be used for display and removal.
     */
    get filteredOccurrences(): FillerWordOccurrence[] {
        if (this.avoidHarshCuts) {
            return this.occurrences.filter(o => !o.isHarshCut);
        }
        return this.occurrences;
    }
    
    /**
     * Check if a word at a given time is in the filler word occurrences.
     * Returns the occurrence if found, null otherwise.
     * Respects avoidHarshCuts setting - only returns non-harsh occurrences when enabled.
     */
    getOccurrenceAtTime(startSec: number): FillerWordOccurrence | null {
        // Use a small epsilon for floating point comparison
        const epsilon = 0.01;
        // Search in filteredOccurrences to respect avoidHarshCuts setting
        return this.filteredOccurrences.find(o => 
            startSec >= (o.startSec - epsilon) && startSec <= (o.endSec + epsilon)
        ) ?? null;
    }
    
    /**
     * Check if a word at a given time is the currently selected filler word.
     * Respects avoidHarshCuts - won't match if current occurrence is harsh and avoid is enabled.
     */
    isCurrentOccurrence(startSec: number): boolean {
        const current = this.currentOccurrence;
        if (!current) return false;
        // If avoidHarshCuts is enabled and current is harsh, don't highlight
        if (this.avoidHarshCuts && current.isHarshCut) return false;
        const epsilon = 0.01;
        return startSec >= (current.startSec - epsilon) && startSec <= (current.endSec + epsilon);
    }
    
    /**
     * Get context words with timestamps for playback preview.
     * Returns words within 2 seconds before and 2 seconds after the filler word.
     */
    getContextWordsWithTimestamps(occurrence: FillerWordOccurrence): {
        wordsBefore: Array<{ text: string; startSec: number; endSec: number }>;
        fillerWord: { text: string; startSec: number; endSec: number };
        wordsAfter: Array<{ text: string; startSec: number; endSec: number }>;
    } | null {
        const epsilon = 0.01;
        const CONTEXT_SECONDS = 2.0; // 2 seconds before and after
        
        const fillerIndex = this.allWords.findIndex(w => 
            Math.abs(w.startSec - occurrence.startSec) < epsilon
        );
        
        if (fillerIndex === -1) return null;
        
        const fillerWord = {
            text: occurrence.word,
            startSec: occurrence.startSec,
            endSec: occurrence.endSec ?? occurrence.startSec,
        };
        
        // Get words within 2 seconds BEFORE the filler word starts
        const contextStartTime = occurrence.startSec - CONTEXT_SECONDS;
        const wordsBefore = this.allWords
            .filter((w, idx) => idx < fillerIndex && w.startSec >= contextStartTime)
            .map(w => ({
                text: w.text,
                startSec: w.startSec,
                endSec: w.endSec ?? w.startSec,
            }));
        
        // Get words within 2 seconds AFTER the filler word ends
        const fillerEndSec = occurrence.endSec ?? occurrence.startSec;
        const contextEndTime = fillerEndSec + CONTEXT_SECONDS;
        const wordsAfter = this.allWords
            .filter((w, idx) => idx > fillerIndex && w.startSec > fillerEndSec + epsilon && w.startSec <= contextEndTime)
            .map(w => ({
                text: w.text,
                startSec: w.startSec,
                endSec: w.endSec ?? w.startSec,
            }));
        
        return { wordsBefore, fillerWord, wordsAfter };
    }
    
    /**
     * Get category info with counts.
     */
    get categoriesWithCounts(): Array<{ id: string; label: string; count: number; enabled: boolean }> {
        return FILLER_WORD_CATEGORIES.map(category => ({
            id: category.id,
            label: category.label,
            count: this.categoryCounts.get(category.id) || 0,
            enabled: this.enabledCategories.has(category.id),
        }));
    }
    
    /**
     * Check if all categories are selected.
     */
    get allCategoriesSelected(): boolean {
        return this.enabledCategories.size === FILLER_WORD_CATEGORIES.length;
    }
    
    /**
     * Check if some (but not all) categories are selected.
     */
    get someCategoriesSelected(): boolean {
        return this.enabledCategories.size > 0 && 
               this.enabledCategories.size < FILLER_WORD_CATEGORIES.length;
    }
    
    /**
     * Remove current occurrence (applies cut).
     * Returns the occurrence that was removed.
     */
    removeCurrent(): FillerWordOccurrence | null {
        const current = this.currentOccurrence;
        if (!current) return null;
        
        // Remove from occurrences list
        this.occurrences = this.occurrences.filter(o => o.id !== current.id);
        
        // Also remove from allWords to keep context accurate
        const epsilon = 0.01;
        this.allWords = this.allWords.filter(w => 
            Math.abs(w.startSec - current.startSec) >= epsilon
        );
        
        // Update category counts
        const categoryId = current.categoryId;
        if (categoryId && this.categoryCounts.has(categoryId)) {
            const count = this.categoryCounts.get(categoryId) || 0;
            this.categoryCounts.set(categoryId, Math.max(0, count - 1));
        }
        
        // Adjust index if needed
        if (this.currentIndex >= this.filteredOccurrences.length) {
            this.currentIndex = Math.max(0, this.filteredOccurrences.length - 1);
        }
        
        return current;
    }
    
    /**
     * Get all occurrences for removal (respects avoidHarshCuts).
     */
    getAllForRemoval(): FillerWordOccurrence[] {
        return [...this.filteredOccurrences];
    }
    
    /**
     * Clear all occurrences (after remove all).
     */
    clearAll() {
        this.occurrences = [];
        this.currentIndex = 0;
    }
}

export const FillerWordStore = new FillerWordStoreImpl();
